import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { PromoCode, PromoDiscountType } from '@/types/promoCodes';

interface PromoCodeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null creates a code; otherwise edits this one. */
  code: PromoCode | null;
  onSaved: () => void;
}

type FieldErrors = Partial<Record<'code' | 'discount_type' | 'discount_value' | 'max_redemptions' | 'expires_on', string>>;

/**
 * Rendered only while open, by the page, so its state starts from the code
 * being edited each time rather than needing an effect to reset it.
 */
export function PromoCodeFormDialog({ open, onOpenChange, code, onSaved }: PromoCodeFormDialogProps) {
  const isEdit = code !== null;
  // Redeemed or awaited by a pending subscription: the API's own statement.
  const isLocked = isEdit && code.lockedFields.length > 0;
  // No fixed-amount code can be made here, and one made another way has no
  // agreed currency, so it is not edited here either.
  const isFixed = isEdit && code.discountType === 'fixed';
  const typeDisabled = isLocked || isFixed;

  const [codeText, setCodeText] = useState(code?.code ?? '');
  const [type, setType] = useState<PromoDiscountType>(code?.discountType ?? 'percentage');
  const [value, setValue] = useState(code ? String(code.discountValue) : '');
  const [limit, setLimit] = useState(code?.maxRedemptions != null ? String(code.maxRedemptions) : '');
  const [expiresOn, setExpiresOn] = useState(code?.expiresOn ?? '');
  const [isActive, setIsActive] = useState(code?.isActive ?? true);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const valueLabel = type === 'percentage' ? 'Percentage off' : type === 'free_period' ? 'Free months' : 'Amount off';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrors({});
    setIsSaving(true);

    // The API reads a null limit or expiry as "none".
    const next = {
      discount_type: type,
      discount_value: Number(value),
      max_redemptions: limit === '' ? null : Number(limit),
      expires_on: expiresOn === '' ? null : expiresOn,
      is_active: isActive,
    };

    try {
      if (isEdit) {
        // Only what changed is sent. The API checks an expiry is today or later
        // whenever it is present, so echoing back a lapsed one would refuse
        // every edit of an expired code. A redeemed code's type and value are
        // locked and refused even unchanged, so they are never sent then.
        const current = {
          discount_type: code.discountType,
          discount_value: code.discountValue,
          max_redemptions: code.maxRedemptions,
          expires_on: code.expiresOn,
          is_active: code.isActive,
        };
        const skipped = typeDisabled ? ['discount_type', 'discount_value'] : [];
        const payload = Object.fromEntries(
          Object.entries(next).filter(([field, v]) => !skipped.includes(field) && v !== current[field as keyof typeof current]),
        );
        if (Object.keys(payload).length === 0) {
          onOpenChange(false);
          return;
        }
        await api.patch(`/promo-codes/${code.id}`, payload);
        toast.success('Promo code updated.');
      } else {
        await api.post('/promo-codes', { code: codeText.trim(), ...next });
        toast.success('Promo code created.');
      }
      onSaved();
      onOpenChange(false);
    } catch (error) {
      const fieldErrors = axios.isAxiosError(error) ? error.response?.data?.errors : undefined;
      const shown = ['code', 'discount_type', 'discount_value', 'max_redemptions', 'expires_on'];
      const shownErrors =
        fieldErrors && typeof fieldErrors === 'object'
          ? Object.entries(fieldErrors).filter(([field]) => shown.includes(field))
          : [];
      if (shownErrors.length > 0) {
        setErrors(Object.fromEntries(shownErrors.map(([field, messages]) => [field, (messages as string[])[0]])));
      } else {
        toast.error(getApiErrorMessage(error));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <DialogHeader>
            <DialogTitle>{isEdit ? `Edit ${code.code}` : 'Add promo code'}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'The code itself cannot be changed.'
                : 'A percentage code discounts every invoice of the subscription it is applied to. A free months code adds months that are never invoiced.'}
            </DialogDescription>
          </DialogHeader>

          {isFixed && (
            <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              Fixed amount codes cannot be edited here. Deactivate it and create a new one instead.
            </p>
          )}

          {isLocked && (
            <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              {code.redemptionsCount > 0
                ? `${code.redemptionsCount} ${code.redemptionsCount === 1 ? 'group has' : 'groups have'} used this code, so what it gives can no longer change. Deactivate it and create a new one instead.`
                : 'A group chose this code at checkout and has not paid yet, so what it gives can no longer change. Deactivate it and create a new one instead.'}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="promo-code">Code</Label>
            <Input id="promo-code" value={codeText} disabled={isEdit} maxLength={40} aria-invalid={!!errors.code}
              onChange={(e) => setCodeText(e.target.value)} />
            {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="promo-type">Type</Label>
              <Select value={type} disabled={typeDisabled} onValueChange={(v) => setType(v as PromoDiscountType)}>
                <SelectTrigger id="promo-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage off</SelectItem>
                  <SelectItem value="free_period">Free months</SelectItem>
                  {isFixed && <SelectItem value="fixed">Fixed amount</SelectItem>}
                </SelectContent>
              </Select>
              {errors.discount_type && <p className="text-xs text-destructive">{errors.discount_type}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-value">{valueLabel}</Label>
              <Input id="promo-value" type="number" min={1} max={type === 'percentage' ? 100 : 24} step={1}
                value={value} disabled={typeDisabled} aria-invalid={!!errors.discount_value}
                onChange={(e) => setValue(e.target.value)} />
              {errors.discount_value && <p className="text-xs text-destructive">{errors.discount_value}</p>}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="promo-limit">Redemption limit</Label>
              <Input id="promo-limit" type="number" min={1} step={1} placeholder="No limit" value={limit}
                aria-invalid={!!errors.max_redemptions} onChange={(e) => setLimit(e.target.value)} />
              {errors.max_redemptions && <p className="text-xs text-destructive">{errors.max_redemptions}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-expiry">Expires on</Label>
              <Input id="promo-expiry" type="date" value={expiresOn} aria-invalid={!!errors.expires_on}
                onChange={(e) => setExpiresOn(e.target.value)} />
              {errors.expires_on && <p className="text-xs text-destructive">{errors.expires_on}</p>}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="promo-active">Active</Label>
              <p className="text-xs text-muted-foreground">
                Switching a code off stops new uses. Groups that already used it keep their discount.
              </p>
            </div>
            <Switch id="promo-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isSaving}>{isEdit ? 'Save changes' : 'Create code'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
